// PropertyReporter Theme System
// Centralized design tokens for consistent UI across the entire application

export const theme = {
  colors: {
    // Core Brand Colors (from PDF Editor)
    background: '#F9F7F2',        // Soft warm background
    surface: '#FFFFFF',           // Clean card/surface color
    navy: '#142C3F',              // Primary dark navy (headers, nav)
    green: '#2F7A4F',             // Accent green (buttons, highlights)
    
    // Text Colors
    textPrimary: '#1E1E1E',       // Main text color
    textSecondary: '#6B7280',     // Secondary text, labels
    textTertiary: '#9CA3AF',      // Subtle text, placeholders
    textInverse: '#FFFFFF',       // Text on dark backgrounds
    
    // UI Colors
    border: '#E5E7EB',            // Subtle borders
    borderLight: '#F3F4F6',       // Very light borders
    divider: 'rgba(0, 0, 0, 0.08)', // Section dividers
    
    // State Colors
    success: '#10B981',           // Success states
    warning: '#F59E0B',           // Warning states
    error: '#EF4444',             // Error states
    info: '#3B82F6',              // Info states
    
    // Semantic Colors
    backgroundLight: '#FAFBFC',   // Light background variations
    surfaceElevated: '#FFFFFF',   // Elevated surfaces (modals, dropdowns)
    overlay: 'rgba(0, 0, 0, 0.5)', // Modal overlays
    
    // Interaction Colors
    hover: 'rgba(47, 122, 79, 0.1)',     // Hover states
    active: 'rgba(47, 122, 79, 0.2)',    // Active states
    focus: 'rgba(47, 122, 79, 0.3)',     // Focus states
    disabled: '#F3F4F6',                  // Disabled states
  },
  
  typography: {
    // Font Stack - Professional, system fonts with Inter as primary
    fontFamily: {
      primary: `'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif`,
      mono: `'SF Mono', 'Monaco', 'Inconsolata', 'Roboto Mono', monospace`,
    },
    
    // Font Sizes (using rem for scalability)
    fontSize: {
      xs: '0.75rem',    // 12px
      sm: '0.875rem',   // 14px
      base: '1rem',     // 16px
      lg: '1.125rem',   // 18px
      xl: '1.25rem',    // 20px
      '2xl': '1.5rem',  // 24px
      '3xl': '1.875rem', // 30px
      '4xl': '2.25rem', // 36px
    },
    
    // Font Weights
    fontWeight: {
      light: 300,
      regular: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
      extrabold: 800,
    },
    
    // Line Heights
    lineHeight: {
      tight: 1.25,
      normal: 1.5,
      relaxed: 1.75,
    },
    
    // Letter Spacing
    letterSpacing: {
      tight: '-0.025em',
      normal: '0',
      wide: '0.025em',
    },
  },
  
  spacing: {
    // Consistent spacing scale (using rem)
    xs: '0.25rem',    // 4px
    sm: '0.5rem',     // 8px
    md: '1rem',       // 16px
    lg: '1.5rem',     // 24px
    xl: '2rem',       // 32px
    '2xl': '3rem',    // 48px
    '3xl': '4rem',    // 64px
    '4xl': '6rem',    // 96px
  },
  
  radius: {
    // Border Radius Scale
    none: '0',
    sm: '4px',
    md: '8px',
    lg: '12px',
    xl: '16px',
    '2xl': '24px',
    full: '9999px',   // Pills/circular
  },
  
  shadow: {
    // Elevation System (iOS/Material inspired)
    none: 'none',
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    base: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    '2xl': '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    soft: '0 4px 12px rgba(0, 0, 0, 0.08)',  // Notion-style soft shadow
    card: '0 2px 8px rgba(0, 0, 0, 0.1)',    // Card shadows
  },
  
  layout: {
    // Layout Constants
    maxWidth: {
      sm: '640px',
      md: '768px',
      lg: '1024px',
      xl: '1280px',
      '2xl': '1536px',
      container: '1200px',  // Main content container
    },
    
    // Sidebar & Navigation
    sidebar: {
      width: '240px',
      widthCollapsed: '64px',
    },
    
    // Header Heights
    header: {
      height: '64px',
      heightLarge: '80px',
    },
    
    // Z-Index Scale
    zIndex: {
      base: 0,
      dropdown: 100,
      overlay: 200,
      modal: 300,
      popover: 400,
      tooltip: 500,
      notification: 600,
    },
  },
  
  animation: {
    // Transition Durations
    duration: {
      fast: '150ms',
      normal: '200ms',
      slow: '300ms',
      slower: '500ms',
    },
    
    // Easing Functions
    easing: {
      ease: 'ease',
      easeIn: 'ease-in',
      easeOut: 'ease-out',
      easeInOut: 'ease-in-out',
      spring: 'cubic-bezier(0.34, 1.56, 0.64, 1)', // Bouncy spring
    },
  },
  
  // Component-specific tokens
  components: {
    button: {
      height: {
        sm: '32px',
        md: '40px',
        lg: '48px',
      },
      padding: {
        sm: '8px 16px',
        md: '12px 24px',
        lg: '16px 32px',
      },
    },
    
    input: {
      height: {
        sm: '32px',
        md: '40px',
        lg: '48px',
      },
      padding: '12px 16px',
    },
    
    card: {
      padding: '24px',
      paddingCompact: '16px',
    },
  },
  
  // Breakpoints for responsive design
  breakpoints: {
    sm: '640px',
    md: '768px',
    lg: '1024px',
    xl: '1280px',
    '2xl': '1536px',
  },
};

// Export individual theme sections for convenience
export const { colors, typography, spacing, radius, shadow, layout, animation, components } = theme;

// Export theme as default
export default theme;
